import { useState, useEffect } from 'react';
import { config } from '../../../config';
import { prepareHeaders } from '../../../configuration/tokenHandling/prepareHeaders';
import { type TableViewTogglesViewType } from '@rio-cloud/rio-uikit/TableViewToggles';
import { IPagination } from '../../../shared/models/IPagination';
import { IOperationalDiscipline } from '../models/OperationalDiscipline';
import { intToMonth } from '../../../shared/utils/formatters';

type UseOperationalDisciplineDataProps = {
  externalViewType: TableViewTogglesViewType;
  assetId: string | undefined;
  offset: number;
};

interface IApiResponse {
  items: IOperationalDisciplineItem[],
  pagination: IPagination
}

interface IOperationalDisciplineItem {
  month: number,
  year: number,
  monthScore: number,
  averageScore: number,
  complianceReviews: number,
  operationalCoherence: number,
  responsibleInsurance: number,
  [key: string]: any;
}

export const useOperationalDisciplineData = ({
assetId, offset}: UseOperationalDisciplineDataProps) => {
  const [ OperationalDisciplineResponseList, setOperationalDisciplineResponseList ] = useState<IOperationalDiscipline[]>([]);
  const [ isLoading, setIsLoading ] = useState(false);
  const [ error, setError ] = useState(null);
  const [pagination, setPagination] = useState<IPagination>();

  useEffect(() => {

    const getOperationalDiscipline = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const headers = await prepareHeaders(new Headers());
        const response = await fetch(config.backend.SCORES_SERVICE + `/v2/relationships/${assetId}/operational-discipline?offset=${offset}&size=50`, {
          method: 'GET',
          headers
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = (await response.json()) as IApiResponse;

        setPagination(data.pagination);

        setOperationalDisciplineResponseList(data.items.map((item: IOperationalDisciplineItem) => {
          const result: IOperationalDiscipline = {
            date: `${intToMonth(item.month)}/${item.year}`,
            year: item.year,
            month: item.month,
            score: item.monthScore,
            averageScore: item.averageScore,
            complianceReviews: item.complianceReviews,
            operationalCoherence: item.operationalCoherence,
            responsibleInsurance: item.responsibleInsurance
          }
          return result;
        }));

      } catch (e: any) {
        console.error('Error fetching relationship:', e);
        setError(e.message);
      } finally {
        setIsLoading(false);
      }
    };
    if(assetId){
      getOperationalDiscipline();
    }
  }, [offset, assetId]);

  return {
    OperationalDisciplineResponseList,
    pagination,
    setPagination,
    isLoading,
    error,
  };
};